(() => {
  const ROOT_SELECTOR = '[data-ultra-pdp-shell] #section-quick-product-v2';
  const mobileMedia = window.matchMedia('(max-width: 1000px)');
  const instances = new Map();
  const optionAliases = {
    color: ['color', 'colour'],
    size: ['size'],
    version: ['helmet type', 'model', 'select a bundle']
  };

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function findOptionPosition(optionNames, aliases) {
    const index = optionNames.findIndex((name) => aliases.includes(normalize(name)));
    return index === -1 ? null : index + 1;
  }

  class UltraMobileVariantAvailability {
    constructor(root) {
      this.root = root;
      this.shell = root.closest('[data-ultra-pdp-shell]');
      this.fixedSize = this.shell?.dataset.ultraFixedSize === 'true';
      this.fixedVersion = this.shell?.dataset.ultraFixedVersion === 'true';
      this.productHandle = root.querySelector('.global-product-handle')?.value || '';
      this.form = root.querySelector('.ProductForm');
      this.floatingSection = document.querySelector('[id*="quick_add_to_cart_v2"]');
      this.productJson = this.form?.querySelector('[data-product-json]');
      this.createdLabels = [];
      this.promptButtons = [];
      this.requiredOption = null;
      this.purchaseSelectionRequired = false;
      this.syncFrame = null;
      this.guideTimer = null;
      this.hiddenVariant = null;
      this.lastSyncedVariantId = null;
      this.variantControls = [];
      this.handleChange = this.handleChange.bind(this);
      this.handleCtaClick = this.handleCtaClick.bind(this);

      if (!this.form || !this.productJson || !this.shell) return;

      try {
        this.product = JSON.parse(this.productJson.textContent).product;
      } catch {
        return;
      }

      if (!this.product?.variants?.length || !this.product?.options?.length) return;

      const optionNames = this.product.options.map((option) => (
        typeof option === 'string' ? option : option.name
      ));
      this.positions = {
        color: findOptionPosition(optionNames, optionAliases.color),
        size: findOptionPosition(optionNames, optionAliases.size),
        version: findOptionPosition(optionNames, optionAliases.version)
      };

      if (Object.values(this.positions).some((position) => position === null)) return;

      this.availableVariants = this.product.variants.filter((variant) => variant.available);
      const allOptionInputs = Array.from(
        this.form.querySelectorAll(
          '.ProductForm__Option input[type="radio"][data-option-position]'
        )
      );
      this.optionInputs = allOptionInputs.filter((input) => (
        !input.closest('.ultra-hidden-option')
      ));
      this.hiddenOptionInputs = allOptionInputs.filter((input) => (
        Boolean(input.closest('.ultra-hidden-option'))
      ));
      this.bundleInputs = Array.from(
        this.form.querySelectorAll(
          '.ProductForm__Option.version .extra-bundle input[type="radio"][data-variant="Helmet Type"]'
        )
      );
      this.managedInputs = Array.from(new Set([
        ...this.optionInputs,
        ...this.hiddenOptionInputs,
        ...this.bundleInputs
      ]));
      this.variantControls = Array.from(
        this.form.querySelectorAll('select[name="id"], input[name="id"]')
      );
      this.initialInputStates = this.managedInputs.map((input) => {
        const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);

        return {
          input,
          disabled: input.disabled,
          inputAriaDisabled: input.getAttribute('aria-disabled'),
          label,
          labelAriaDisabled: label?.getAttribute('aria-disabled') || null
        };
      });

      this.decorateOptions();
      this.form.addEventListener('change', this.handleChange);
      this.form.addEventListener('click', this.handleCtaClick, true);
      this.floatingSection?.addEventListener('click', this.handleCtaClick, true);
      this.scheduleSync();
      this.root.dataset.ultraMobileAvailabilityReady = 'true';
    }

    inputsAt(position) {
      return this.optionInputs.filter(
        (input) => Number(input.dataset.optionPosition) === position
      );
    }

    optionAt(position) {
      return this.inputsAt(position)[0]?.closest('.ProductForm__Option') || null;
    }

    selectedValue(position) {
      const visibleValue = this.inputsAt(position).find(
        (input) => input.checked && !input.disabled
      )?.value;
      if (visibleValue) return visibleValue;

      return this.hiddenInputsAt(position).find(
        (input) => input.checked && !input.disabled
      )?.value || null;
    }

    hiddenInputsAt(position) {
      return this.hiddenOptionInputs.filter(
        (input) => Number(input.dataset.optionPosition) === position
      );
    }

    visibleSelections() {
      const selections = new Map();

      this.optionInputs.forEach((input) => {
        if (!input.checked || input.disabled) return;
        selections.set(Number(input.dataset.optionPosition), input.value);
      });

      return selections;
    }

    hiddenOptionGroups() {
      const groups = new Map();

      this.hiddenOptionInputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        const group = input.closest('.ultra-hidden-option');
        if (!group || !position) return;

        if (!groups.has(position)) groups.set(position, { position, inputs: [] });
        groups.get(position).inputs.push(input);
      });

      return [...groups.values()].sort((a, b) => a.position - b.position);
    }

    variantOptionValue(variant, position) {
      return variant?.options?.[position - 1] ?? variant?.[`option${position}`];
    }

    syncHiddenOptions() {
      const groups = this.hiddenOptionGroups();
      this.hiddenVariant = null;
      if (!groups.length) return;

      const visibleSelections = this.visibleSelections();
      const candidates = this.availableVariants.filter((variant) => (
        [...visibleSelections].every(([position, value]) => (
          this.variantMatches(variant, position, value)
        )) && groups.every(({ position }) => (
          this.variantOptionValue(variant, position) !== undefined
        ))
      ));
      if (!candidates.length) return;

      const currentHiddenValues = new Map(
        groups.map(({ position, inputs }) => [
          position,
          inputs.find((input) => input.checked)?.value || null
        ])
      );
      const candidate = candidates.reduce((best, variant) => {
        if (!best) return variant;

        const score = groups.reduce((total, { position }) => (
          total + (this.variantOptionValue(variant, position) === currentHiddenValues.get(position) ? 1 : 0)
        ), 0);
        const bestScore = groups.reduce((total, { position }) => (
          total + (this.variantOptionValue(best, position) === currentHiddenValues.get(position) ? 1 : 0)
        ), 0);

        return score > bestScore ? variant : best;
      }, null);

      let hiddenSelectionChanged = false;
      let changedHiddenInput = null;
      groups.forEach(({ position, inputs }) => {
        const value = this.variantOptionValue(candidate, position);
        const selectedInput = inputs.find((input) => input.value === value);
        if (!selectedInput) return;

        inputs.forEach((input) => {
          const checked = input === selectedInput;
          if (input.checked !== checked) {
            input.checked = checked;
            hiddenSelectionChanged = true;
            if (checked) changedHiddenInput = input;
          }
        });
      });

      const selections = this.visibleSelections();
      groups.forEach(({ position, inputs }) => {
        const selected = inputs.find((input) => input.checked && !input.disabled);
        if (selected) selections.set(position, selected.value);
      });
      const complete = this.product.options.every((_, index) => selections.has(index + 1));
      if (!complete) return;

      const actualVariant = this.availableVariants.find((variant) => (
        [...selections].every(([position, value]) => (
          this.variantMatches(variant, position, value)
        ))
      ));
      if (!actualVariant) return;

      this.hiddenVariant = actualVariant;
      if (this.hiddenInputsAt(this.positions.version).length) {
        this.purchaseSelectionRequired = false;
      }
      if (hiddenSelectionChanged && changedHiddenInput) {
        const event = new Event('change', { bubbles: true });
        event.ultraHiddenVariantSync = true;
        changedHiddenInput.dispatchEvent(event);
      }
      this.syncHostVariant(actualVariant);
    }

    syncHostVariant(variant) {
      const variantId = String(variant?.id ?? '');
      const control = this.variantControls?.[0];
      if (!variantId || !control) return;
      if (control.value === variantId) {
        this.lastSyncedVariantId = variantId;
        return;
      }
      if (this.lastSyncedVariantId === variantId) return;

      control.value = variantId;
      this.lastSyncedVariantId = variantId;
    }

    ensureFixedSizeSelection() {
      if (!this.fixedSize) return;

      const sizeInputs = this.inputsAt(this.positions.size);
      if (sizeInputs.length !== 1) return;

      const input = sizeInputs[0];
      if (!input.disabled && !input.checked) input.checked = true;
    }

    ensureFixedVersionSelection() {
      if (!this.fixedVersion) return;

      const inputs = this.purchaseInputs();
      if (inputs.length !== 1) return;

      inputs[0].checked = true;
      this.purchaseSelectionRequired = false;
    }

    purchaseInputs() {
      return [
        ...this.inputsAt(this.positions.version),
        ...this.bundleInputs
      ];
    }

    selectedPurchaseInput() {
      if (this.purchaseSelectionRequired && !this.fixedVersion) return null;

      if (this.hiddenInputsAt(this.positions.version).length) {
        return this.hiddenVariant
          ? this.hiddenInputsAt(this.positions.version).find((input) => input.checked) || null
          : null;
      }

      return this.purchaseInputs().find((input) => (
        input.checked && (this.fixedVersion || !input.disabled)
      )) || null;
    }

    requirePurchaseSelection() {
      this.purchaseSelectionRequired = true;
    }

    decorateOptions() {
      const soldOutLabel = this.shell.dataset.ultraSoldOutLabel || 'Sold out';

      this.managedInputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);

        if (!label || position === this.positions.color) return;
        if (label.querySelector('.UltraOptionAvailabilityLabel')) return;

        const status = document.createElement('span');
        status.className = 'UltraOptionAvailabilityLabel';
        status.textContent = soldOutLabel;
        label.appendChild(status);
        this.createdLabels.push(status);
      });
    }

    variantMatches(variant, position, value) {
      return this.variantOptionValue(variant, position) === value;
    }

    hasAvailableVariant(position, value, fixedSelections = []) {
      return this.availableVariants.some((variant) => {
        if (!this.variantMatches(variant, position, value)) return false;

        return fixedSelections.every(([fixedPosition, fixedValue]) => (
          this.variantMatches(variant, fixedPosition, fixedValue)
        ));
      });
    }

    sizeKey(value) {
      return String(value || '').trim().split(/\s+/)[0].replace('/', '-');
    }

    isWinterBundle(input) {
      return normalize(input.value).includes('winter kit');
    }

    winterLinerSize(size) {
      const normalizedSize = this.sizeKey(size).toUpperCase();
      if (this.productHandle !== 'lumos-nyxel') return normalizedSize;

      return {
        S: 'S',
        M: 'M-L',
        L: 'XL'
      }[normalizedSize] || normalizedSize;
    }

    bundleOptions(input) {
      return Array.from(
        input.closest('.extra-bundle')?.querySelectorAll('.hidden-selector option') || []
      );
    }

    bundleOptionMatches(input, option, color, size) {
      if (!color || !size) return false;
      if (option.dataset.option1 !== color) return false;
      if (this.sizeKey(option.dataset.option2) !== this.sizeKey(size)) return false;

      return !this.isWinterBundle(input) ||
        this.sizeKey(option.dataset.option3).toUpperCase() === this.winterLinerSize(size);
    }

    hasAvailableBundle(input, color, size) {
      return this.bundleOptions(input).some((option) => (
        !option.disabled && this.bundleOptionMatches(input, option, color, size)
      ));
    }

    hasAvailableBundleColor(input, color) {
      return this.bundleOptions(input).some((option) => {
        if (option.disabled || option.dataset.option1 !== color) return false;

        return !this.isWinterBundle(input) ||
          this.sizeKey(option.dataset.option3).toUpperCase() ===
            this.winterLinerSize(option.dataset.option2);
      });
    }

    setInputAvailability(input, isAvailable, isAwaitingSelection = false) {
      const item = input.closest('.HorizontalList__Item');
      const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const isDisabled = isAwaitingSelection || !isAvailable;

      input.disabled = isDisabled;
      input.setAttribute('aria-disabled', String(isDisabled));
      item?.classList.toggle(
        'is-ultra-mobile-unavailable',
        !isAwaitingSelection && !isAvailable
      );
      item?.classList.toggle(
        'is-ultra-mobile-awaiting-selection',
        isAwaitingSelection
      );

      if (label) label.setAttribute('aria-disabled', String(isDisabled));
    }

    updateColorOptions() {
      this.inputsAt(this.positions.color).forEach((input) => {
        const hasMainVariant = this.hasAvailableVariant(
          this.positions.color,
          input.value
        );
        const hasBundleVariant = this.bundleInputs.some((bundleInput) => (
          this.hasAvailableBundleColor(bundleInput, input.value)
        ));

        this.setInputAvailability(input, hasMainVariant || hasBundleVariant);
      });
    }

    updateSizeOptions(selectedColor) {
      const isAwaitingSelection = !selectedColor;

      this.inputsAt(this.positions.size).forEach((input) => {
        const hasMainVariant = !isAwaitingSelection && this.hasAvailableVariant(
          this.positions.size,
          input.value,
          [[this.positions.color, selectedColor]]
        );
        const hasBundleVariant = !isAwaitingSelection && this.bundleInputs.some(
          (bundleInput) => this.hasAvailableBundle(
            bundleInput,
            selectedColor,
            input.value
          )
        );

        this.setInputAvailability(
          input,
          hasMainVariant || hasBundleVariant,
          isAwaitingSelection
        );
      });
    }

    updatePurchaseOptions(selectedColor, selectedSize) {
      const isAwaitingSelection = !selectedColor || !selectedSize;

      this.inputsAt(this.positions.version).forEach((input) => {
        const isAvailable = !isAwaitingSelection && this.hasAvailableVariant(
          this.positions.version,
          input.value,
          [
            [this.positions.color, selectedColor],
            [this.positions.size, selectedSize]
          ]
        );

        this.setInputAvailability(input, isAvailable, isAwaitingSelection);
      });

      this.bundleInputs.forEach((input) => {
        const isAvailable = !isAwaitingSelection &&
          this.hasAvailableBundle(input, selectedColor, selectedSize);

        this.setInputAvailability(input, isAvailable, isAwaitingSelection);
      });
    }

    clearInvalidSelections(inputs) {
      inputs.forEach((input) => {
        if (input.checked && input.disabled) input.checked = false;
      });
    }

    setRequiredState(prompt, position) {
      this.requiredOption = this.optionAt(position);
      this.root.classList.add('is-ultra-mobile-selection-incomplete');
      this.shell.classList.add('is-ultra-mobile-selection-incomplete');
      this.root.dataset.ultraMobileRequiredPosition = String(position);
      this.updatePromptButtons(prompt);
      window.LumosCartOperations?.syncProductForm?.(this.form);
    }

    setCompleteState() {
      this.requiredOption = null;
      this.root.classList.remove('is-ultra-mobile-selection-incomplete');
      this.shell.classList.remove('is-ultra-mobile-selection-incomplete');
      delete this.root.dataset.ultraMobileRequiredPosition;
      this.clearGuidance();
      this.removePromptButtons();
      window.LumosCartOperations?.syncProductForm?.(this.form);
    }

    removePromptButtons() {
      this.promptButtons.forEach((button) => button.remove());
      this.promptButtons = [];
    }

    updatePromptButtons(label) {
      const buttons = [
        this.form.querySelector('.ProductForm__BuyButtons .ProductForm__AddToCart'),
        this.floatingSection?.querySelector('.ProductForm__AddToCart.mobile')
      ].filter(Boolean);

      this.promptButtons = this.promptButtons.filter((button) => button.isConnected);

      buttons.forEach((button) => {
        let promptButton = button.parentElement?.querySelector(
          ':scope > .UltraMobileCtaPromptButton'
        );

        if (!promptButton) {
          promptButton = document.createElement('button');
          promptButton.type = 'button';
          promptButton.className = [
            'button',
            'Button',
            'Button--primary',
            'Button--full',
            'UltraMobileCtaPromptButton'
          ].join(' ');
          button.insertAdjacentElement('afterend', promptButton);
          this.promptButtons.push(promptButton);
        }

        promptButton.textContent = label;
      });
    }

    sync() {
      if (!mobileMedia.matches) return;

      this.syncHiddenOptions();
      this.updateColorOptions();
      this.clearInvalidSelections(this.inputsAt(this.positions.color));
      const selectedColor = this.selectedValue(this.positions.color);

      this.updateSizeOptions(selectedColor);
      this.clearInvalidSelections(this.inputsAt(this.positions.size));
      this.ensureFixedSizeSelection();
      const selectedSize = this.selectedValue(this.positions.size);

      this.updatePurchaseOptions(selectedColor, selectedSize);
      this.clearInvalidSelections(this.purchaseInputs());
      this.ensureFixedVersionSelection();
      const selectedPurchase = this.selectedPurchaseInput();

      if (!selectedColor) {
        this.setRequiredState(
          this.shell.dataset.ultraChooseColorLabel || 'Choose color',
          this.positions.color
        );
      } else if (!selectedSize && !this.fixedSize) {
        this.setRequiredState(
          this.shell.dataset.ultraChooseSizeLabel || 'Choose size',
          this.positions.size
        );
      } else if (!selectedPurchase && !this.fixedVersion) {
        this.setRequiredState(
          this.shell.dataset.ultraChooseVersionLabel || 'Choose version',
          this.positions.version
        );
      } else {
        this.setCompleteState();
      }
    }

    scheduleSync() {
      if (!mobileMedia.matches || this.syncFrame !== null) return;

      this.syncFrame = window.requestAnimationFrame(() => {
        this.syncFrame = null;
        this.sync();
      });
    }

    clearGuidance() {
      window.clearTimeout(this.guideTimer);
      this.form.querySelectorAll(
        '.ProductForm__Option.is-ultra-mobile-option-required'
      ).forEach((option) => {
        option.classList.remove('is-ultra-mobile-option-required');
      });
    }

    guideToRequiredOption() {
      if (!this.requiredOption) return;

      this.clearGuidance();
      const target = this.requiredOption;
      target.classList.add('is-ultra-mobile-option-required');
      const galleryHeight = Number.parseFloat(
        window.getComputedStyle(this.root).getPropertyValue(
          '--ultra-mobile-gallery-height'
        )
      );
      const visibleOffset = (Number.isFinite(galleryHeight) ? galleryHeight : 220) + 16;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(
          0,
          window.scrollY + target.getBoundingClientRect().top - visibleOffset
        ),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
      this.guideTimer = window.setTimeout(() => {
        target.classList.remove('is-ultra-mobile-option-required');
      }, 1200);
    }

    handleChange(event) {
      if (!event.target.matches('input[type="radio"]')) return;
      if (!this.managedInputs.includes(event.target)) return;
      if (event.ultraHiddenVariantSync) return;

      const position = Number(event.target.dataset.optionPosition);
      const isPurchaseInput = position === this.positions.version ||
        this.bundleInputs.includes(event.target);
      const isFixedSizeInput = this.fixedSize && position === this.positions.size;
      if (isPurchaseInput && event.isTrusted) {
        this.purchaseSelectionRequired = false;
      }

      if (!isPurchaseInput && !isFixedSizeInput && !this.fixedVersion && event.isTrusted) {
        this.requirePurchaseSelection();
      }

      this.clearGuidance();
      this.scheduleSync();
    }

    handleCtaClick(event) {
      if (!mobileMedia.matches) return;

      const label = event.target.closest('label[for]');
      const labelInput = label && this.form.querySelector(
        `#${CSS.escape(label.getAttribute('for'))}`
      );

      if (
        event.isTrusted &&
        labelInput &&
        this.purchaseInputs().includes(labelInput) &&
        !labelInput.disabled &&
        this.purchaseSelectionRequired
      ) {
        this.purchaseSelectionRequired = false;
        this.scheduleSync();
      }

      const button = event.target.closest('.UltraMobileCtaPromptButton');
      if (!button) return;
      if (!this.form.contains(button) && !this.floatingSection?.contains(button)) return;
      if (!this.root.classList.contains('is-ultra-mobile-selection-incomplete')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.guideToRequiredOption();
    }

    destroy() {
      this.form?.removeEventListener('change', this.handleChange);
      this.form?.removeEventListener('click', this.handleCtaClick, true);
      this.floatingSection?.removeEventListener('click', this.handleCtaClick, true);
      if (this.syncFrame !== null) window.cancelAnimationFrame(this.syncFrame);
      this.clearGuidance();
      this.removePromptButtons();
      this.root.classList.remove('is-ultra-mobile-selection-incomplete');
      this.shell?.classList.remove('is-ultra-mobile-selection-incomplete');
      delete this.root.dataset.ultraMobileRequiredPosition;
      delete this.root.dataset.ultraMobileAvailabilityReady;
      window.LumosCartOperations?.syncProductForm?.(this.form);

      this.initialInputStates.forEach((state) => {
        state.input.disabled = state.disabled;
        state.input.closest('.HorizontalList__Item')?.classList.remove(
          'is-ultra-mobile-unavailable',
          'is-ultra-mobile-awaiting-selection'
        );

        if (state.inputAriaDisabled === null) {
          state.input.removeAttribute('aria-disabled');
        } else {
          state.input.setAttribute('aria-disabled', state.inputAriaDisabled);
        }

        if (state.label) {
          if (state.labelAriaDisabled === null) {
            state.label.removeAttribute('aria-disabled');
          } else {
            state.label.setAttribute('aria-disabled', state.labelAriaDisabled);
          }
        }
      });
      this.createdLabels.forEach((label) => label.remove());
      this.hiddenVariant = null;
      this.lastSyncedVariantId = null;
    }
  }

  class UltraMobilePurchaseFlow {
    constructor(root) {
      this.root = root;
      this.galleryContainer = root.querySelector('.product-img-container');
      this.gallery = root.querySelector('[data-ultra-mobile-gallery]');
      this.form = root.querySelector('.ProductForm');
      this.productRoot = root.querySelector('.Product');
      this.header = document.getElementById('shopify-section-header');
      this.floatingSection = document.querySelector('[id*="quick_add_to_cart_v2"]');
      this.useGalleryDots = root.dataset.mobileGalleryDots === 'true';
      this.dots = document.createElement('div');
      this.dots.className = 'UltraMobileGalleryDots';
      this.dots.setAttribute('aria-label', 'Product images');
      this.dotSwiperBindings = new Map();
      this.galleryObserver = new MutationObserver(() => this.updateGalleryControls());
      this.compactHeight = 220;
      this.compactTriggerY = 0;
      this.scrollFrame = null;
      this.resizeTimer = null;
      this.setupFrame = null;
      this.sizePlaceholder = null;
      this.sizeOption = null;
      this.availability = null;
      this.pendingColorScrollPosition = null;
      this.scrollRestoreId = 0;
      this.sentinel = document.createElement('span');
      this.sentinel.className = 'UltraMobileGallerySentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.compensation = document.createElement('span');
      this.compensation.className = 'UltraMobileGalleryCompensation';
      this.compensation.setAttribute('aria-hidden', 'true');
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleGalleryClick = this.handleGalleryClick.bind(this);
      this.handleColorChange = this.handleColorChange.bind(this);
      this.rememberColorScrollPosition = this.rememberColorScrollPosition.bind(this);
      this.syncFloatingBarHeight = this.syncFloatingBarHeight.bind(this);

      if (!this.galleryContainer || !this.gallery || !this.form || !this.productRoot) return;

      this.readOptions();
      this.galleryContainer.addEventListener('click', this.handleGalleryClick);
      document.addEventListener('pointerdown', this.rememberColorScrollPosition, true);
      document.addEventListener('change', this.rememberColorScrollPosition, true);
      document.addEventListener('change', this.handleColorChange);
      mobileMedia.addEventListener('change', this.handleMediaChange);
      window.addEventListener('resize', this.handleResize, { passive: true });
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      if (this.floatingSection && 'ResizeObserver' in window) {
        this.floatingResizeObserver = new ResizeObserver(this.syncFloatingBarHeight);
        this.floatingResizeObserver.observe(this.floatingSection);
      }
      this.setup();
      this.root.dataset.ultraMobilePurchaseReady = 'true';
    }

    readOptions() {
      const productJson = this.form.querySelector('[data-product-json]');
      let product;

      try {
        product = JSON.parse(productJson?.textContent || '{}').product;
      } catch {
        product = null;
      }

      const optionNames = (product?.options || []).map((option) => (
        String(typeof option === 'string' ? option : option.name).trim().toLowerCase()
      ));
      this.sizePosition = optionNames.indexOf('size') + 1;
      this.versionPosition = optionNames.findIndex(
        (name) => name === 'helmet type' || name === 'model' || name === 'select a bundle'
      ) + 1;
      this.optionInputs = Array.from(
        this.form.querySelectorAll(
          '.ProductForm__Option input[type="radio"][data-option-position]'
        )
      ).filter((input) => !input.closest('.ultra-hidden-option'));
    }

    optionElement(position) {
      return this.optionInputs.find(
        (input) => Number(input.dataset.optionPosition) === position
      )?.closest('.ProductForm__Option') || null;
    }

    reorderOptions() {
      if (this.sizePlaceholder || !this.sizePosition || !this.versionPosition) return;

      const sizeOption = this.optionElement(this.sizePosition);
      const versionOption = this.optionElement(this.versionPosition);
      if (
        !sizeOption ||
        !versionOption ||
        sizeOption.parentNode !== versionOption.parentNode ||
        sizeOption.nextElementSibling === versionOption
      ) {
        return;
      }

      this.sizeOption = sizeOption;
      this.sizePlaceholder = document.createComment('ultra-mobile-size-option');
      sizeOption.parentNode.insertBefore(this.sizePlaceholder, sizeOption);
      versionOption.parentNode.insertBefore(sizeOption, versionOption);
    }

    restoreOptions() {
      if (!this.sizeOption || !this.sizePlaceholder?.parentNode) return;

      this.sizePlaceholder.parentNode.insertBefore(this.sizeOption, this.sizePlaceholder);
      this.sizePlaceholder.remove();
      this.sizePlaceholder = null;
      this.sizeOption = null;
    }

    headerOffset() {
      if (!this.header) return 0;
      return Math.max(0, Math.round(this.header.getBoundingClientRect().height));
    }

    setStickyOffset() {
      this.root.style.setProperty(
        '--ultra-mobile-sticky-top',
        `${this.headerOffset()}px`
      );
    }

    syncFloatingBarHeight() {
      if (!mobileMedia.matches) {
        this.root.style.removeProperty('--ultra-mobile-floating-bar-height');
        return;
      }

      const height = this.floatingSection?.getBoundingClientRect().height || 0;
      this.root.style.setProperty(
        '--ultra-mobile-floating-bar-height',
        `${Math.round(height)}px`
      );
    }

    attachGalleryPlaceholders() {
      if (!this.sentinel.isConnected) this.galleryContainer.before(this.sentinel);
      if (!this.compensation.isConnected) this.galleryContainer.after(this.compensation);
    }

    measureGallery() {
      const wasCompact = this.root.classList.contains(
        'is-ultra-mobile-gallery-compact'
      );
      if (wasCompact) this.applyCompactState(false);

      const expandedHeight = Math.round(this.galleryContainer.getBoundingClientRect().height);
      const configuredHeight = Number.parseFloat(
        window.getComputedStyle(this.root).getPropertyValue(
          '--ultra-mobile-gallery-height'
        )
      );
      this.compactHeight = Number.isFinite(configuredHeight)
        ? configuredHeight
        : 220;
      const compensation = this.useGalleryDots ? 0 : Math.max(0, expandedHeight - this.compactHeight);
      const sentinelTop = window.scrollY + this.sentinel.getBoundingClientRect().top;
      this.compactTriggerY = Math.max(this.useGalleryDots ? 48 : 0, sentinelTop + compensation - this.headerOffset());
      this.root.style.setProperty(
        '--ultra-mobile-gallery-compensation',
        `${compensation}px`
      );

      if (wasCompact) this.applyCompactState(true);
    }

    applyCompactState(isCompact) {
      this.root.classList.toggle('is-ultra-mobile-gallery-compact', isCompact);
      document.body.classList.toggle(
        'ultra-mobile-gallery-compact-active',
        isCompact
      );
      this.root.style.setProperty(
        '--ultra-mobile-sticky-top',
        isCompact ? '0px' : `${this.headerOffset()}px`
      );
      if (this.header) {
        if (isCompact) {
          this.header.setAttribute('inert', '');
          this.header.setAttribute('aria-hidden', 'true');
          this.header.dataset.ultraMobileInert = 'true';
        } else if (this.header.dataset.ultraMobileInert === 'true') {
          this.header.removeAttribute('inert');
          this.header.removeAttribute('aria-hidden');
          delete this.header.dataset.ultraMobileInert;
        }
      }
      this.updateGalleryControls();
    }

    updateCompactState() {
      const isCompact = this.root.classList.contains(
        'is-ultra-mobile-gallery-compact'
      );
      const hysteresis = 24;

      if (!isCompact && window.scrollY >= this.compactTriggerY) {
        this.applyCompactState(true);
      } else if (isCompact && window.scrollY <= this.compactTriggerY - hysteresis) {
        this.applyCompactState(false);
      }
    }

    handleScroll() {
      if (!mobileMedia.matches || this.scrollFrame !== null) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateCompactState();
      });
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        if (!mobileMedia.matches) return;

        this.applyCompactState(false);
        this.setStickyOffset();
        this.syncFloatingBarHeight();
        window.requestAnimationFrame(() => {
          if (!mobileMedia.matches) return;
          this.measureGallery();
          this.updateCompactState();
          this.updateGalleryControls();
        });
      }, 100);
    }

    selectedColorInput() {
      return this.form.querySelector(
        '.ColorSwatchList input[name="option-0"]:checked'
      );
    }

    usesVariantGallery() {
      return this.root.dataset.versionGalleryEnabled === 'true';
    }

    selectedVariantGallery() {
      return this.gallery.querySelector(
        '[data-ebike-version-gallery-group]:not([hidden]) [data-ebike-version-gallery-main]'
      );
    }

    galleryForColorInput(input) {
      if (this.usesVariantGallery()) return this.selectedVariantGallery();
      if (!this.isUltraColorInput(input)) return null;

      const colorClass = `alt-${normalize(input.value)
        .replace(/ & /g, '-')
        .replace(/ /g, '-')
        .replace(/\//g, '-')}`;

      return Array.from(
        this.gallery.querySelectorAll('[data-ultra-mobile-main]')
      ).find((gallery) => gallery.classList.contains(colorClass)) || null;
    }

    syncActiveGallery(input = this.selectedColorInput()) {
      const activeGallery = this.galleryForColorInput(input);
      if (!activeGallery) return null;

      // The variant-gallery controller owns active/hidden state for Color +
      // Size + Version. The mobile purchase flow only consumes that state.
      if (this.usesVariantGallery()) return activeGallery;

      this.gallery.querySelectorAll('[data-ultra-mobile-main]').forEach((gallery) => {
        gallery.classList.toggle('active', gallery === activeGallery);
      });
      this.gallery.querySelectorAll('[data-ultra-mobile-thumbs]').forEach((thumbnails) => {
        thumbnails.classList.toggle('active', activeGallery.contains(thumbnails));
      });

      return activeGallery;
    }

    activeGallery() {
      return this.galleryForColorInput(this.selectedColorInput()) ||
        this.gallery.querySelector('[data-ultra-mobile-main].active') ||
        this.gallery.querySelector('[data-ultra-mobile-main]');
    }

    refreshGallery(gallery = this.activeGallery()) {
      if (!gallery) return;

      const thumbnails = gallery.querySelector('[data-ultra-mobile-thumbs]');
      thumbnails?.swiper?.update();
      gallery.swiper?.update();
      gallery.swiper?.thumbs?.update?.();
    }

    resetGalleryToFirstSlide(gallery) {
      if (!gallery) return;

      this.refreshGallery(gallery);
      const thumbnails = gallery.querySelector('[data-ultra-mobile-thumbs]');

      if (gallery.swiper?.slides?.length) {
        gallery.swiper.slideTo(0, 0);
      } else {
        thumbnails?.querySelector('.swiper-slide')?.click();
      }

      if (thumbnails?.swiper?.slides?.length) {
        thumbnails.swiper.slideTo(0, 0);
      }

      this.refreshGallery(gallery);
    }

    openImageZoom(image) {
      if (this.zoomDialog || !image?.getAttribute('src')) return;
      const dialog = document.createElement('dialog');
      dialog.className = 'UltraMobileImageZoom';
      dialog.setAttribute('aria-label', image.alt || 'Product image');
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'UltraMobileImageZoomClose';
      close.setAttribute('aria-label', 'Close enlarged image');
      close.textContent = '×';
      const fullImage = document.createElement('img');
      fullImage.src = image.getAttribute('src');
      fullImage.alt = image.alt;
      dialog.append(close, fullImage);
      close.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('UltraMobileZoomOpen');
        dialog.remove();
        this.zoomDialog = null;
        if (image.isConnected) image.focus({ preventScroll: true });
      }, { once: true });
      document.body.append(dialog);
      this.zoomDialog = dialog;
      document.documentElement.classList.add('UltraMobileZoomOpen');
      dialog.showModal();
    }

    syncGalleryDots() {
      if (!mobileMedia.matches || !this.mobileActive || !this.useGalleryDots) return;
      const gallery = this.activeGallery();
      const slides = gallery?.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide') || [];
      const swiper = gallery?.swiper;
      if (this.root.dataset.mobileGalleryZoom === 'true') {
        Array.from(slides).filter(slide => slide.tagName === 'IMG').forEach(image => {
          image.setAttribute('role', 'button');
          image.setAttribute('tabindex', '0');
          image.setAttribute('aria-label', `Enlarge ${image.alt || 'product image'}`);
          if (!image.dataset.ultraZoomKeyBound) {
            image.addEventListener('keydown', event => {
              if (mobileMedia.matches && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                this.openImageZoom(image);
              }
            });
            image.dataset.ultraZoomKeyBound = 'true';
          }
        });
      }
      if (swiper && !this.dotSwiperBindings.has(swiper)) {
        const update = () => this.syncGalleryDots();
        swiper.on('slideChange transitionEnd', update);
        this.dotSwiperBindings.set(swiper, update);
      }
      if (this.dots.children.length !== slides.length) {
        this.dots.replaceChildren(...Array.from(slides, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.dataset.ultraMobileDot = String(index);
          dot.setAttribute('aria-label', `Show product image ${index + 1}`);
          return dot;
        }));
      }
      const activeIndex = swiper?.activeIndex ?? Math.max(0, Array.from(slides).findIndex(slide => slide.classList.contains('swiper-slide-active')));
      Array.from(this.dots.children).forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === activeIndex));
      });
      this.dots.hidden = slides.length < 2;
    }

    updateGalleryControls() {
      this.syncGalleryDots();
      this.gallery.querySelectorAll('[data-ultra-mobile-main]').forEach((gallery) => {
        const swiper = gallery.swiper;
        const previous = gallery.querySelector('[data-ultra-mobile-prev]');
        const next = gallery.querySelector('[data-ultra-mobile-next]');
        const isActive = gallery === this.activeGallery();
        if (!previous || !next) return;

        const slides = Array.from(
          gallery.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide')
        );
        const hasMultipleSlides = slides.length > 1;
        previous.disabled = !isActive || !hasMultipleSlides;
        next.disabled = !isActive || !hasMultipleSlides;

        if (swiper && !gallery.dataset.ultraMobileSwiperBound) {
          swiper.on('slideChange transitionEnd', () => this.updateGalleryControls());
          gallery.dataset.ultraMobileSwiperBound = 'true';
        }
      });
    }

    isUltraColorInput(target) {
      return Boolean(
        target &&
        typeof target.matches === 'function' &&
        target.matches('.ColorSwatchList input[name="option-0"]') &&
        this.productRoot.contains(target)
      );
    }

    colorInputFromTarget(target) {
      if (!target || typeof target.closest !== 'function') return null;

      const input = target.closest('.ColorSwatchList input[name="option-0"]');
      if (this.isUltraColorInput(input)) return input;

      const label = target.closest('.ColorSwatchList label[for]');
      if (!label || !this.productRoot.contains(label)) return null;

      const labelledInput = document.getElementById(label.htmlFor);
      return this.isUltraColorInput(labelledInput) ? labelledInput : null;
    }

    rememberColorScrollPosition(event) {
      if (!mobileMedia.matches) return;

      const startsPointerSequence = [
        'pointerdown'
      ].includes(event.type);
      if (
        !this.colorInputFromTarget(event.target) ||
        (this.pendingColorScrollPosition && !startsPointerSequence)
      ) {
        return;
      }

      this.pendingColorScrollPosition = {
        left: window.scrollX,
        top: window.scrollY
      };
    }

    restoreColorScrollPosition() {
      const position = this.pendingColorScrollPosition;
      if (!position || !mobileMedia.matches) return;

      const restoreId = ++this.scrollRestoreId;
      const restore = () => {
        if (restoreId !== this.scrollRestoreId || !mobileMedia.matches) return;
        window.scrollTo(position.left, position.top);
      };

      window.requestAnimationFrame(() => {
        restore();
        window.requestAnimationFrame(() => {
          restore();
          if (restoreId === this.scrollRestoreId) {
            this.pendingColorScrollPosition = null;
          }
        });
      });
    }

    handleColorChange(event) {
      if (!this.isUltraColorInput(event.target)) return;
      const colorInput = event.target;
      const gallery = this.usesVariantGallery()
        ? null
        : this.syncActiveGallery(colorInput);
      this.restoreColorScrollPosition();
      window.requestAnimationFrame(() => {
        if (!mobileMedia.matches) return;
        if (gallery) this.resetGalleryToFirstSlide(gallery);
        this.updateGalleryControls();
      });
    }

    handleGalleryClick(event) {
      if (!mobileMedia.matches) return;

      const image = event.target.closest('img.product-showcase-img');
      const activeGallery = this.activeGallery();
      if (image && this.useGalleryDots && this.root.dataset.mobileGalleryZoom === 'true' &&
          activeGallery?.contains(image) && activeGallery.swiper?.allowClick !== false &&
          !image.closest('[data-ultra-mobile-thumbs]')) {
        event.preventDefault();
        event.stopPropagation();
        this.openImageZoom(image);
        return;
      }
      const dot = event.target.closest('[data-ultra-mobile-dot]');
      if (dot) {
        event.preventDefault();
        const gallery = this.activeGallery();
        const index = Number(dot.dataset.ultraMobileDot);
        if (gallery?.swiper) gallery.swiper.slideTo(index);
        else gallery?.querySelectorAll('[data-ultra-mobile-thumbs] .swiper-slide')[index]?.click();
        this.syncGalleryDots();
        return;
      }
      const button = event.target.closest(
        '[data-ultra-mobile-prev], [data-ultra-mobile-next]'
      );
      if (!button) return;

      event.preventDefault();
      const isPrevious = button.matches('[data-ultra-mobile-prev]');
      const gallery = this.syncActiveGallery() ||
        button.closest('[data-ultra-mobile-main]');
      if (!gallery) return;

      this.refreshGallery(gallery);
      this.updateGalleryControls();
      const activeButton = gallery.querySelector(
        isPrevious ? '[data-ultra-mobile-prev]' : '[data-ultra-mobile-next]'
      );
      if (activeButton?.disabled) return;

      const swiper = gallery.swiper;
      if (swiper && isPrevious) {
        if (swiper.isBeginning) {
          swiper.slideTo(swiper.slides.length - 1);
        } else {
          swiper.slidePrev();
        }
      } else if (swiper) {
        if (swiper.isEnd) {
          swiper.slideTo(0);
        } else {
          swiper.slideNext();
        }
      } else {
        const slides = Array.from(
          gallery.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide')
        );
        const currentIndex = Math.max(
          0,
          slides.findIndex((slide) => slide.classList.contains('swiper-slide-active'))
        );
        const direction = isPrevious ? -1 : 1;
        const targetIndex = (
          currentIndex + direction + slides.length
        ) % slides.length;
        const thumbnails = gallery.querySelectorAll(
          '[data-ultra-mobile-thumbs] .swiper-slide'
        );
        thumbnails[targetIndex]?.click();
      }
      window.requestAnimationFrame(() => this.updateGalleryControls());
    }

    setup() {
      if (!mobileMedia.matches) {
        this.teardown();
        return;
      }

      this.mobileActive = true;
      document.body.classList.add('ultra-mobile-purchase-flow-active');
      this.root.classList.add('UltraMobilePurchaseFlow');
      this.attachGalleryPlaceholders();
      if (this.useGalleryDots) {
        this.galleryContainer.append(this.dots);
        this.galleryObserver.observe(this.gallery, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
      }
      this.reorderOptions();
      if (!this.availability) {
        this.availability = new UltraMobileVariantAvailability(this.root);
      }
      const initialGallery = this.syncActiveGallery();
      this.refreshGallery(initialGallery);
      this.setStickyOffset();
      this.syncFloatingBarHeight();
      if (this.setupFrame !== null) window.cancelAnimationFrame(this.setupFrame);
      this.setupFrame = window.requestAnimationFrame(() => {
        this.setupFrame = null;
        if (!mobileMedia.matches) return;
        const gallery = this.syncActiveGallery();
        this.refreshGallery(gallery);
        this.measureGallery();
        this.updateCompactState();
        this.updateGalleryControls();
      });
    }

    teardown() {
      this.zoomDialog?.close();
      this.mobileActive = false;
      this.galleryObserver.disconnect();
      this.dotSwiperBindings.forEach((callback, swiper) => swiper.off?.('slideChange transitionEnd', callback));
      this.dotSwiperBindings.clear();
      this.dots.remove();
      if (this.setupFrame !== null) {
        window.cancelAnimationFrame(this.setupFrame);
        this.setupFrame = null;
      }
      if (this.scrollFrame !== null) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
      window.clearTimeout(this.resizeTimer);
      this.pendingColorScrollPosition = null;
      this.scrollRestoreId += 1;
      this.applyCompactState(false);
      this.availability?.destroy();
      this.availability = null;
      this.restoreOptions();
      this.root.classList.remove('UltraMobilePurchaseFlow');
      this.root.style.removeProperty('--ultra-mobile-gallery-compensation');
      this.root.style.removeProperty('--ultra-mobile-floating-bar-height');
      this.root.style.removeProperty('--ultra-mobile-sticky-top');
      this.sentinel.remove();
      this.compensation.remove();
      document.body.classList.remove(
        'ultra-mobile-purchase-flow-active',
        'ultra-mobile-gallery-compact-active'
      );
    }

    handleMediaChange() {
      this.setup();
    }

    destroy() {
      this.teardown();
      mobileMedia.removeEventListener('change', this.handleMediaChange);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleScroll);
      this.galleryContainer?.removeEventListener('click', this.handleGalleryClick);
      document.removeEventListener('pointerdown', this.rememberColorScrollPosition, true);
      document.removeEventListener('change', this.rememberColorScrollPosition, true);
      document.removeEventListener('change', this.handleColorChange);
      this.floatingResizeObserver?.disconnect();
      delete this.root.dataset.ultraMobilePurchaseReady;
    }
  }

  function initialize(scope = document) {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root.dataset.ultraMobilePurchaseReady === 'true') return;
      const instance = new UltraMobilePurchaseFlow(root);
      if (root.dataset.ultraMobilePurchaseReady === 'true') {
        instances.set(root, instance);
      }
    });
  }

  function destroyWithin(scope) {
    instances.forEach((instance, root) => {
      if (root === scope || scope.contains?.(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
